import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, MessageCircle, FileText, Ticket, Loader2 } from 'lucide-react';
import { generatePDF } from '@/utils/generatePDF';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const firstName = (name) => String(name || 'there').trim().split(/\s+/)[0] || 'there';

const waLink = (traveller, kind, doc) => {
  const cc = String(traveller?.countryCode || '91').replace(/\D/g, '') || '91';
  const ph = String(traveller?.phone || '').replace(/\D/g, '');
  const number = ph.length > 10 ? ph : cc + ph;
  const msg =
    `Hi ${firstName(traveller?.fullName)}, please find your ${kind} ` +
    `for ${doc.destination || 'your'} trip ${doc.tripRef || ''}. — Travcations`;
  return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
};

const DocLine = ({ icon: Icon, label, doc, testidBase, onDownload, downloading, waHref }) => (
  <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 bg-white" data-testid={`${testidBase}-card`}>
    <div className="flex items-center gap-3 min-w-0">
      <span className="h-9 w-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-slate-500">{label}</div>
        <div className="text-sm font-bold text-slate-900 truncate" data-testid={`${testidBase}-ref`}>
          {doc.ref}
        </div>
        <div className="text-[11px] text-slate-400 truncate">{doc.destination || '—'}</div>
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <button
        type="button"
        onClick={onDownload}
        disabled={downloading}
        data-testid={`${testidBase}-download-btn`}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
      >
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        PDF
      </button>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testidBase}-whatsapp-btn`}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
      >
        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
      </a>
    </div>
  </div>
);

const TravellerComms = ({ ucc, traveller }) => {
  const [data, setData] = useState({ voucher: null, invoice: null });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/clients/${ucc}/latest-documents`, { withCredentials: true });
      setData(res.data || { voucher: null, invoice: null });
    } catch (e) {
      // non-blocking — panel just stays empty
    } finally {
      setLoading(false);
    }
  }, [ucc]);

  useEffect(() => { load(); }, [load]);

  const downloadVoucher = async (v) => {
    setDownloading('voucher');
    try {
      await generatePDF({
        pageUrl: `${window.location.origin}/voucher/${v.voucherId}`,
        filename: `${v.tripRef || 'voucher'}-Voucher.pdf`,
      });
    } catch (e) {
      toast.error('Could not generate voucher PDF');
    } finally {
      setDownloading('');
    }
  };

  const downloadInvoice = async (inv) => {
    setDownloading('invoice');
    try {
      await generatePDF({
        pageUrl: `${window.location.origin}/invoice-preview/${encodeURIComponent(inv.invoiceNumber)}`,
        filename: `${inv.invoiceNumber || 'invoice'}.pdf`.replace(/[\\/]/g, '-'),
      });
    } catch (e) {
      toast.error('Could not generate invoice PDF');
    } finally {
      setDownloading('');
    }
  };

  const { voucher, invoice } = data;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-3" data-testid="comms-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading documents…
      </div>
    );
  }

  if (!voucher && !invoice) {
    return (
      <p className="text-sm text-slate-400 py-3" data-testid="comms-empty">
        No vouchers or invoices yet for this traveller.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="traveller-comms">
      {voucher && (
        <DocLine
          icon={Ticket}
          label="Latest Voucher"
          testidBase="voucher"
          doc={{ ref: voucher.trvRef || voucher.tripRef, destination: voucher.destination }}
          downloading={downloading === 'voucher'}
          onDownload={() => downloadVoucher(voucher)}
          waHref={waLink(traveller, 'voucher', voucher)}
        />
      )}
      {invoice && (
        <DocLine
          icon={FileText}
          label="Latest Invoice"
          testidBase="invoice"
          doc={{ ref: invoice.invoiceNumber, destination: invoice.destination }}
          downloading={downloading === 'invoice'}
          onDownload={() => downloadInvoice(invoice)}
          waHref={waLink(traveller, 'invoice', invoice)}
        />
      )}
    </div>
  );
};

export default TravellerComms;
